import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Alert,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Category as CategoryIcon,
  Stop as StopIcon,
  ExitToApp as ExitIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { tradePlansApi } from '../../api/tradePlans';
import DWLFLoader from '../../components/DWLFLoader';

const TradePlanViewer = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);

  const categories = [
    { value: 'swing_trading', label: 'Swing Trading' },
    { value: 'day_trading', label: 'Day Trading' },
    { value: 'scalping', label: 'Scalping' },
    { value: 'position_trading', label: 'Position Trading' },
    { value: 'general', label: 'General' }
  ];

  const timeframes = [
    { value: '1m', label: '1 Minute' },
    { value: '5m', label: '5 Minutes' },
    { value: '15m', label: '15 Minutes' },
    { value: '1h', label: '1 Hour' },
    { value: '4h', label: '4 Hours' },
    { value: '1d', label: 'Daily' },
    { value: '1w', label: 'Weekly' }
  ];

  useEffect(() => {
    loadPlan();
  }, [planId]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const response = await tradePlansApi.getTradePlan(planId);
      setPlan(response);
    } catch (err) {
      setError('Failed to load trade plan');
      console.error('Error loading trade plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      const newName = `${plan.name} - Copy`;
      await tradePlansApi.duplicatePlan(plan.planId, newName);
      navigate('/trade-plans');
    } catch (err) {
      setError('Failed to duplicate trade plan');
      console.error('Error duplicating trade plan:', err);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      swing_trading: 'primary',
      day_trading: 'secondary',
      scalping: 'success',
      position_trading: 'warning',
      general: 'default'
    };
    return colors[category] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="medium" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate('/trade-plans')}>
          Back to Trade Plans
        </Button>
      </Box>
    );
  }

  if (!plan) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          Trade plan not found
        </Typography>
        <Button onClick={() => navigate('/trade-plans')}>
          Back to Trade Plans
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={4}>
        <IconButton 
          onClick={() => navigate('/trade-plans')} 
          sx={{ 
            mr: 2, 
            borderRadius: 0,
            border: '1px solid var(--dwlf-ink-10)',
            '&:hover': {
              borderColor: 'var(--dwlf-accent)',
              backgroundColor: 'var(--dwlf-accent-05)',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography 
          sx={{ 
            flexGrow: 1,
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: { xs: '1.5rem', md: '1.75rem' },
            fontWeight: 600,
            letterSpacing: '-0.02em',
          }}
        >
          {plan.name}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/trade-plans/${plan.planId}/edit`)}
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderRadius: 0,
              borderColor: 'var(--dwlf-ink-20)',
              color: 'var(--dwlf-ink-60)',
              '&:hover': {
                borderColor: 'var(--dwlf-accent)',
                color: 'var(--dwlf-accent)',
                backgroundColor: 'transparent',
              },
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<DuplicateIcon />}
            onClick={handleDuplicate}
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderRadius: 0,
              borderColor: 'var(--dwlf-ink-20)',
              color: 'var(--dwlf-ink-60)',
              '&:hover': {
                borderColor: 'var(--dwlf-accent)',
                color: 'var(--dwlf-accent)',
                backgroundColor: 'transparent',
              },
            }}
          >
            Duplicate
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/trades/new?planId=${plan.planId}`)}
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderRadius: 0,
              backgroundColor: 'var(--dwlf-accent)',
              color: 'var(--dwlf-bg)',
              '&:hover': {
                backgroundColor: 'var(--dwlf-text)',
              },
            }}
          >
            Use This Plan
          </Button>
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr' }, gap: 3 }}>
        {/* Plan Overview */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plan Overview
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Category
                  </Typography>
                  <Chip
                    label={categories.find(c => c.value === plan.category)?.label || plan.category}
                    color={getCategoryColor(plan.category)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Timeframe
                  </Typography>
                  <Typography variant="body1">
                    {timeframes.find(t => t.value === plan.timeframe)?.label || plan.timeframe}
                  </Typography>
                </Box>
                {plan.usageCount > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Usage
                    </Typography>
                    <Typography variant="body1">
                      {plan.usageCount} trade(s)
                    </Typography>
                  </Box>
                )}
                {plan.successRate && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Success Rate
                    </Typography>
                    <Typography variant="body1">
                      {(plan.successRate * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {new Date(plan.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Plan Details */}
        <Box>
          {/* Description */}
          {plan.description && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1">
                  {plan.description}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Entry Criteria */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Entry Criteria
              </Typography>
              <Typography variant="body1">
                {plan.entryCriteria || 'No entry criteria specified'}
              </Typography>
            </CardContent>
          </Card>

          {/* Stop Loss Rules */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Initial Stop Loss Rules
              </Typography>
              {plan.initialStopRules ? (
                <Paper sx={{ p: 2, backgroundColor: 'var(--dwlf-accent-05)', border: '1px solid var(--dwlf-accent-15)' }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography 
                        sx={{ 
                          fontFamily: '"Space Mono", monospace',
                          fontSize: '0.7rem',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: 'var(--dwlf-ink-50)',
                          mb: 0.5,
                        }}
                      >
                        Type
                      </Typography>
                      <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.9rem' }}>
                        {plan.initialStopRules.type}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography 
                        sx={{ 
                          fontFamily: '"Space Mono", monospace',
                          fontSize: '0.7rem',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: 'var(--dwlf-ink-50)',
                          mb: 0.5,
                        }}
                      >
                        Value
                      </Typography>
                      <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.9rem' }}>
                        {plan.initialStopRules.value}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography 
                        sx={{ 
                          fontFamily: '"Space Mono", monospace',
                          fontSize: '0.7rem',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: 'var(--dwlf-ink-50)',
                          mb: 0.5,
                        }}
                      >
                        Description
                      </Typography>
                      <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.9rem' }}>
                        {plan.initialStopRules.description}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No stop loss rules specified
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Take Profit Rules */}
          {plan.takeProfitRules && plan.takeProfitRules.length > 0 ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Take Profit Rules
                </Typography>
                <Stack spacing={2}>
                  {plan.takeProfitRules.map((rule, index) => (
                    <Paper key={index} sx={{ p: 2, backgroundColor: 'var(--dwlf-up-05)', border: '1px solid var(--dwlf-up-15)' }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <Typography 
                            sx={{ 
                              fontFamily: '"Space Mono", monospace',
                              fontSize: '0.7rem',
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              color: 'var(--dwlf-ink-50)',
                              mb: 0.5,
                            }}
                          >
                            Type
                          </Typography>
                          <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.9rem' }}>
                            {rule.type}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <Typography 
                            sx={{ 
                              fontFamily: '"Space Mono", monospace',
                              fontSize: '0.7rem',
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              color: 'var(--dwlf-ink-50)',
                              mb: 0.5,
                            }}
                          >
                            Value
                          </Typography>
                          <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.9rem' }}>
                            {rule.value}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography 
                            sx={{ 
                              fontFamily: '"Space Mono", monospace',
                              fontSize: '0.7rem',
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              color: 'var(--dwlf-ink-50)',
                              mb: 0.5,
                            }}
                          >
                            Description
                          </Typography>
                          <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.9rem' }}>
                            {rule.description}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No take profit rules specified
            </Typography>
          )}

          {/* Management Rules */}
          {plan.managementRules && plan.managementRules.length > 0 ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Position Management Rules
                </Typography>
                <List>
                  {plan.managementRules.map((rule, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <SettingsIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={rule} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No management rules specified
            </Typography>
          )}

          {/* Exit Criteria */}
          {plan.exitCriteria && plan.exitCriteria.length > 0 ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Exit Criteria
                </Typography>
                <List>
                  {plan.exitCriteria.map((criteria, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <ExitIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={criteria} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No exit criteria specified
            </Typography>
          )}

          {/* Tags */}
          {plan.tags && plan.tags.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tags
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {plan.tags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TradePlanViewer;
